import * as React from 'react';
import { action, observable } from 'mobx';
import { observer } from 'mobx-react';
import { Button, Col, ControlLabel, FormControl, FormGroup, Panel, Modal, InputGroup } from 'react-bootstrap';
import * as ApiTypes from '../../../models/ApiTypes';
import * as ApiFactory from '../../../models/ApiTypeFactory';
import { bind, inject } from '../../../utils/decorators';
import FaIcon from '../../site/FaIcon';
import { UiStore } from '../../../stores/UiStore';
import AuftragAdresseUebernehmen from './AuftragAdresseUebernehmen';

interface IAuftragAdresseErfassenProps {
  caption: string;
  adresse: ApiTypes.IAdresse;
  adressArt: ApiTypes.AdressArt;
  onUpdateAdresse: (adresse: ApiTypes.IAdresse) => void;
}

/** Komponente zur Eingabe einer Adresse für die Auftragsanlage, inkl. optionaler Möglichkeit zur Übernahme der Daten aus Adressen oder Untersuchungsstellen */
@observer
export default class AuftragAdresseErfassen extends React.Component<IAuftragAdresseErfassenProps> {

  @inject(UiStore) uiStore: UiStore;

  @observable adresse: ApiTypes.IAdresse = ApiFactory.leereAdresse();

  @observable modalVisible: boolean = false;

  @bind @action showModal() {
    this.modalVisible = true;
  }

  @bind @action hideModal() {
    this.modalVisible = false;
  }

  @bind @action setAdresse(adresse: ApiTypes.IAdresse) {
    this.adresse = adresse;
    this.props.onUpdateAdresse(this.adresse);
  }

  @bind async uebernehmeAdresse(adresse: ApiTypes.IAdresse) {
    this.setAdresse(adresse);
    this.hideModal();
  }

  @bind @action handleNameChanged(event: React.FormEvent<FormControl>) {
    this.adresse.name = (event.target as HTMLInputElement).value;
    this.props.onUpdateAdresse(this.adresse);
  }

  @bind @action handleZusatzChanged(event: React.FormEvent<FormControl>) {
    this.adresse.zusatz = (event.target as HTMLInputElement).value;
    this.props.onUpdateAdresse(this.adresse);
  }

  @bind @action handleStrasseChanged(event: React.FormEvent<FormControl>) {
    this.adresse.strasse = (event.target as HTMLInputElement).value;
    this.props.onUpdateAdresse(this.adresse);
  }

  @bind @action handlePlzChanged(event: React.FormEvent<FormControl>) {
    this.adresse.plz = (event.target as HTMLInputElement).value;
    this.props.onUpdateAdresse(this.adresse);
  }

  @bind @action handleOrtChanged(event: React.FormEvent<FormControl>) {
    this.adresse.ort = (event.target as HTMLInputElement).value;
    this.props.onUpdateAdresse(this.adresse);
  }

  @bind @action handleTelefonnummerChanged(event: React.FormEvent<FormControl>) {
    this.adresse.tel1 = (event.target as HTMLInputElement).value;
    this.props.onUpdateAdresse(this.adresse);
  }

  @bind @action handleEmailChanged(event: React.FormEvent<FormControl>) {
    this.adresse.email = (event.target as HTMLInputElement).value;
    this.props.onUpdateAdresse(this.adresse);
  }

  @bind @action handleEmail2Changed(event: React.FormEvent<FormControl>) {
    this.adresse.email2 = (event.target as HTMLInputElement).value;
    this.props.onUpdateAdresse(this.adresse);
  }

  @bind @action handleUstLoeschen(event: React.FormEvent<FormControl>) {
    this.adresse.untersuchungsstellenkennung = '';
    this.adresse.adressid = undefined;
  }

  renderHeader() {
    const portalAdmin = this.uiStore.userProfile && this.uiStore.userProfile.portaladmin;
    return (
      <div>
        <div className="panel-title pull-left">
          {this.props.caption}
        </div>
        <div className="panel-title pull-right">
            <Button bsStyle="default" onClick={this.showModal}><FaIcon icon="address-book-o" /> {portalAdmin ? 'Aus Adresse' : 'Aus Untersuchungsstelle'}</Button>
        </div>
        <div className="clearfix"/>
      </div>
    );
  }

  render() {
    const portalAdmin = this.uiStore.userProfile && this.uiStore.userProfile.portaladmin;
    return (
      <Panel className="panel-header-buttoned" header={this.renderHeader()}>

        <FormGroup validationState={this.adresse.name.trim() === '' ? 'error' : undefined} controlId="Name">
          <Col componentClass={ControlLabel} sm={3}>Name</Col>
          <Col sm={9}>
            <FormControl type="text" label="Name" value={this.adresse.name || ''} onChange={this.handleNameChanged} />
          </Col>
        </FormGroup>

        <FormGroup controlId="Name">
            <Col componentClass={ControlLabel} sm={3}>Zusatz</Col>
            <Col sm={9}>
              <FormControl type="text" label="Zusatz" value={this.adresse.zusatz || ''} onChange={this.handleZusatzChanged} />
            </Col>
          </FormGroup>

        <FormGroup controlId="Strasse">
          <Col componentClass={ControlLabel} sm={3}>Straße</Col>
          <Col sm={9}>
            <FormControl type="text" label="Strasse" value={this.adresse.strasse || ''} onChange={this.handleStrasseChanged} />
          </Col>
        </FormGroup>

        <FormGroup controlId="PLZ">
          <Col componentClass={ControlLabel} sm={3}>PLZ</Col>
          <Col sm={9}>
            <FormControl type="text" label="PLZ" value={this.adresse.plz || ''} onChange={this.handlePlzChanged} />
          </Col>
        </FormGroup>

        <FormGroup controlId="Ort">
          <Col componentClass={ControlLabel} sm={3}>Ort</Col>
          <Col sm={9}>
            <FormControl type="text" label="Ort" value={this.adresse.ort || ''} onChange={this.handleOrtChanged} />
          </Col>
        </FormGroup>

        <FormGroup controlId="telefonnummer">
          <Col componentClass={ControlLabel} sm={3}>Telefonnummer</Col>
          <Col sm={9}>
            <FormControl type="text" label="Telefonnummer" placeholder="Optional" value={this.adresse.tel1 || ''} onChange={this.handleTelefonnummerChanged} />
          </Col>
        </FormGroup>

        <FormGroup controlId="email">
          <Col componentClass={ControlLabel} sm={3}>E-Mail</Col>
          <Col sm={9}>
            <FormControl type="text" label="E-Mail" placeholder="Optional" value={this.adresse.email || ''} onChange={this.handleEmailChanged} />
          </Col>
        </FormGroup>

        {/* Erfassen der Buchhaltungs E-Mail macht nur beim Auftraggeber Sinn, weil die Rechnung an ihn geht */}
        {(this.props.adressArt === ApiTypes.AdressArt.Auftraggeber) &&
        <FormGroup controlId="EmailBuchhaltung">
          <Col componentClass={ControlLabel} sm={3}>E-Mail Buchhaltung</Col>
          <Col sm={9}>
            <FormControl type="text" label="EmailBuchhaltung" placeholder="Optional" value={this.adresse.email2 || ''} onChange={this.handleEmail2Changed} />
          </Col>
        </FormGroup>
        }

        {this.adresse.untersuchungsstellenkennung &&
          <FormGroup controlId="zugeordneteust">
            <Col componentClass={ControlLabel} sm={3}>Zugeordnete USt.</Col>
            <Col sm={9}>
              <InputGroup>
                <FormControl type="text" label="Untersuchungsstellenkennung" placeholder="Optional" value={this.adresse.untersuchungsstellenkennung} readOnly={true}/>
                <InputGroup.Button>
                    <Button
                      bsStyle="default"
                      className="btn-group"
                      onClick={this.handleUstLoeschen}>
                      <FaIcon icon="trash" />
                    </Button>
                </InputGroup.Button>
              </InputGroup>
            </Col>
          </FormGroup>
        }

        {/* Die Funktion zur Übernahme der Adressdaten wird modal angezeigt */}

        <Modal show={this.modalVisible} onHide={this.hideModal} className="modal-vertical-centered" backdrop={'static'}>
          <Modal.Header closeButton={true}>
            <Modal.Title>Adresse übernehmen</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {portalAdmin ?
              <div>Adressdaten können entweder über die Kennung einer ÜO oder über eine Adress-ID in den Auftrag übernommen werden.</div>
            :
              <div>Adressdaten können aus der Untersuchungsstellenkennung einer ÜO übernommen werden. Der Auftrag wird dann dieser Untersuchungsstelle zugeordnet.</div>
            }
            <AuftragAdresseUebernehmen onUebernehme={this.uebernehmeAdresse} adressArt={ApiTypes.AdressArt.Untersuchungsstelle}/>
            {/* Nur interne Mitarbeiter dürfen auch aus AdressIDs übernehmen */}
            {portalAdmin && <AuftragAdresseUebernehmen onUebernehme={this.uebernehmeAdresse} adressArt={ApiTypes.AdressArt.Auftraggeber}/>}
          </Modal.Body>
          <Modal.Footer>
            <Button onClick={this.hideModal} bsStyle="default">Abbrechen</Button>
          </Modal.Footer>
        </Modal>

      </Panel>
    );
  }

}



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/components/auftraege/adressen/AuftragAdresseErfassen.tsx